import { CaretLeftIcon } from "@phosphor-icons/react/dist/csr/CaretLeft";
import { useEffect, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Separator } from "@volca/ui/components/ui/separator";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  canResolvePersistentEdit,
  getOverrideKind,
} from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import {
  useGetAiScriptCellOverrideHistory,
  useRestoreOverrideVersion,
} from "@/payroll/ai-script/query";
import {
  AI_SPREADSHEET_OVERRIDE_REVIEW_REASON,
  type IAiScriptEditedCellSelection,
  type IAiSpreadsheetCellLocation,
  type IAiSpreadsheetSheet,
} from "@/payroll/ai-script/types";
import { HistoryEntry } from "./history-entry";
import { OriginalValueEntry } from "./original-value-entry";
import { OverrideRow } from "./override-row";
import { RowPicker } from "./row-picker";
import {
  LoadError,
  SectionLabel,
  showPersistentEditMutationError,
} from "./shared";

interface FocusedEditViewProps {
  focus: IAiScriptEditedCellSelection;
  businessId: string | null;
  runId: string;
  sheets: Pick<IAiSpreadsheetSheet, "id" | "editable">[] | undefined;
  onShowAll: () => void;
  onGoToCell: (cell: IAiSpreadsheetCellLocation) => void;
  onWorkbookMutated?: () => void;
}

const getFocusedEditTitle = (
  hasCell: boolean,
  coordinate: string,
  isAmbiguous: boolean,
) => {
  if (hasCell) return `Cell ${coordinate}`;
  return isAmbiguous ? "Matched multiple rows" : "Row not on this run";
};

const getHistoryCountLabel = (count: number) => {
  if (count === 0) return "No earlier versions";
  return `${count} earlier ${count === 1 ? "version" : "versions"}`;
};

export const FocusedEditView = ({
  focus,
  businessId,
  runId,
  sheets,
  onShowAll,
  onGoToCell,
  onWorkbookMutated,
}: FocusedEditViewProps) => {
  const { override: focusedOverride, coordinate } = focus;
  const { data, isLoading, isError, refetch } =
    useGetAiScriptCellOverrideHistory(
      businessId,
      runId,
      focusedOverride.override_key,
    );
  // Prefer the CURRENT override from the freshly-fetched history, so the panel
  // updates live when the same cell is edited again with the sidebar open — the
  // `focus` prop is only a snapshot from when the row was opened.
  const override =
    (data?.overrides ?? []).find((entry) => entry.current) ?? focusedOverride;
  const cell = override.cell;
  const priorVersions = (data?.overrides ?? []).filter(
    (entry) => !entry.current,
  );
  const isAmbiguous =
    override.review_reason === AI_SPREADSHEET_OVERRIDE_REVIEW_REASON.ambiguous;
  const canResolve = sheets ? canResolvePersistentEdit(override, sheets) : null;

  // One confirmation dialog serves the whole history list; a version is pending
  // until the user confirms (going back discards the newer edits on top of it).
  const restore = useRestoreOverrideVersion(businessId);
  const [pendingTargetId, setPendingTargetId] = useState<string | null>(null);

  useEffect(() => {
    if (canResolve !== true) setPendingTargetId(null);
  }, [canResolve]);

  const confirmRestore = () => {
    if (!pendingTargetId || canResolve !== true) return;
    restore.mutate(
      { runId, overrideId: override.id, targetVersionId: pendingTargetId },
      {
        onSuccess: () => {
          setPendingTargetId(null);
          onWorkbookMutated?.();
          onShowAll();
        },
        onError: (error) => {
          setPendingTargetId(null);
          if (
            showPersistentEditMutationError(
              "Couldn't restore this version",
              error,
            )
          ) {
            onShowAll();
          }
        },
      },
    );
  };

  return (
    <Stack className="w-full" align="start" gap={4}>
      <Button variant="ghost" onClick={onShowAll} className="-ml-2 text-subtle">
        <CaretLeftIcon data-icon="inline-start" />
        All edits
      </Button>

      <Text
        variant="title"
        className="min-w-0 truncate"
        title={getFocusedEditTitle(!!cell, coordinate, isAmbiguous)}
      >
        {getFocusedEditTitle(!!cell, coordinate, isAmbiguous)}
      </Text>

      <OverrideRow
        override={override}
        businessId={businessId}
        runId={runId}
        kind={getOverrideKind(override)}
        canResolve={canResolve}
        onGoToCell={onGoToCell}
        onResolved={onShowAll}
        onWorkbookMutated={onWorkbookMutated}
      />

      {isAmbiguous && canResolve ? (
        <>
          <Separator />
          <RowPicker
            override={override}
            businessId={businessId}
            runId={runId}
            sheets={sheets ?? []}
            onGoToCell={onGoToCell}
            onResolved={onShowAll}
            onWorkbookMutated={onWorkbookMutated}
          />
        </>
      ) : null}

      <Separator />
      <Stack
        className="w-full"
        gap={4}
        direction="row"
        align="baseline"
        justify="between"
      >
        <SectionLabel>Edit history</SectionLabel>
        <Text variant="caption" tone="muted">
          {getHistoryCountLabel(priorVersions.length)}
        </Text>
      </Stack>
      {isLoading ? (
        <Stack
          className="w-full"
          align="start"
          gap={2}
          role="status"
          aria-label="Loading history"
        >
          <Skeleton className="h-14 w-full rounded-2xl" />
          <Skeleton className="h-14 w-full rounded-2xl" />
        </Stack>
      ) : isError ? (
        <LoadError
          message="Couldn't load this edit's history."
          onRetry={() => void refetch()}
        />
      ) : (
        <Stack className="w-full" align="start" gap={2}>
          {priorVersions.map((entry) => (
            <HistoryEntry
              key={entry.id}
              version={entry}
              canRestore={canResolve === true}
              isRestoring={
                restore.isPending &&
                restore.variables?.targetVersionId === entry.id
              }
              pending={pendingTargetId === entry.id}
              onRestore={() => setPendingTargetId(entry.id)}
              onConfirm={confirmRestore}
              onCancel={() => setPendingTargetId(null)}
            />
          ))}
          <OriginalValueEntry
            override={override}
            businessId={businessId}
            runId={runId}
            canResolve={canResolve}
            onResolved={onShowAll}
            onWorkbookMutated={onWorkbookMutated}
          />
        </Stack>
      )}
    </Stack>
  );
};
